import type { QuoteRegion } from '@/types'

/**
 * Regional legal entities a quote can be issued from, with the labels shown in
 * the UI. These keys MUST match `pdfConfig.quoteRegions` on the backend — each
 * one selects the company name/address block printed on the quote PDF.
 *
 * Deliberately a typed constant rather than a database picklist: a picklist
 * value with no matching PDF template would silently render the wrong entity
 * on a customer-facing document.
 */
export const QUOTE_REGION_OPTIONS: ReadonlyArray<{ value: QuoteRegion; label: string }> = [
  { value: 'canada', label: 'Canada — GrackleDocs Inc.' },
  { value: 'australia', label: 'Australia — GrackleDocs Australia Pty Ltd' },
  { value: 'usa', label: 'United States — GrackleDocs USA' },
]

/**
 * Label shown when no region is set: the entity follows the account's country.
 *
 * LEGACY ONLY. New quotes always store a region (defaulting to Canada), so this
 * appears solely on quotes created before that change, which still hold a blank
 * region. It has to keep existing: those quotes resolve their entity from the
 * account's country, and dropping the option would leave their stored value
 * unrepresentable in the select.
 */
export const QUOTE_REGION_AUTO_LABEL = 'Auto (by account country)'

/**
 * The currency each issuing entity normally bills in. Mirrors
 * `pdfConfig.quoteRegionCurrency` on the backend. A quote whose currency
 * doesn't match its region is allowed, but the user must acknowledge it.
 */
export const QUOTE_REGION_CURRENCY: Record<QuoteRegion, string> = {
  canada: 'CAD',
  australia: 'AUD',
  usa: 'USD',
}

/**
 * Returns the expected currency for a region, or null when the region is
 * blank/auto (the entity isn't pinned yet, so there's nothing to compare).
 */
export function expectedCurrencyForRegion(region?: string | null): string | null {
  if (!region) return null
  return QUOTE_REGION_CURRENCY[region as QuoteRegion] ?? null
}

/**
 * True when an explicitly chosen region and currency disagree. Blank region
 * (auto) never mismatches — the entity is resolved later from the account.
 */
export function isRegionCurrencyMismatch(
  region?: string | null,
  currency?: string | null
): boolean {
  const expected = expectedCurrencyForRegion(region)
  return !!expected && !!currency && expected !== currency
}

/** Human-readable explanation used by the warning shown to the user. */
export function regionCurrencyWarning(region?: string | null, currency?: string | null): string {
  const expected = expectedCurrencyForRegion(region)
  const label = QUOTE_REGION_OPTIONS.find((r) => r.value === region)?.label ?? region
  return `This quote is issued from ${label}, which normally bills in ${expected}, but the currency is set to ${currency}. The quote will show ${currency} amounts with the ${expected === 'CAD' ? 'Canadian' : expected === 'AUD' ? 'Australian' : 'US'} entity's address.`
}

/** Short label for display contexts (detail page, lists). */
export function quoteRegionLabel(region?: string | null): string {
  if (!region) return QUOTE_REGION_AUTO_LABEL
  return QUOTE_REGION_OPTIONS.find((r) => r.value === region)?.label ?? region
}
